import React from 'react';
import { KeyboardShortcutState } from '../redux/types';
import keycode from 'keycode';
import { Input } from '../../../Components/Input';
import { Button, ButtonProps } from '../../../Components/Button';
import { Tooltip } from '../../../Components/Tooltip';
import { styled } from 'baseui';
import { shortcutToString } from '../utils/shortcutToString';
const Wrapper = styled('div', {
	position: 'relative',
	width: '120px',
	display: 'flex',
	alignItems: 'center',
	gap: '4px',
});
const NarrowWrapper = styled('div', {
	width: '60%',
});
function removeKeyFromObject(obj: Record<any, any>, val: string) {
	const { [val]: _, ...rest } = obj;
	return rest;
}
function getNormalizedKeycode(event: React.KeyboardEvent<any>) {
	let keycodeValue = keycode(event.keyCode).toLowerCase();
	if (keycodeValue === 'left command' || keycodeValue === 'right command') {
		keycodeValue = 'meta';
	}
	return keycodeValue;
}
export const InputCaptureKeyboardShortcut: React.FC<{
	state: KeyboardShortcutState;
	onComplete(newVal: string): void;
	isOverridable?: boolean;
	resetButtonProps?: ButtonProps;
	hasConflict?: boolean;
} & React.ComponentProps<'input'>> = ({
	state,
	onComplete,
	resetButtonProps = {},
	hasConflict = false,
	isOverridable,
	...rest
}) => {
		const { className, ...restWithoutClassName } = rest;
		const [isRecording, setIsRecording] = React.useState(false);
		const [pressedKeycodes, setPressedKeycodes] = React.useState<Record<string, true>>({});
		const [currentlyPressedKeycodes, setCurrentlyPressedKeycodes] = React.useState<Record<string, true>>({});
		const pressedKeysToString = (map: Record<string, true>) => Object.keys(map).join('+');
		const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = event => {
			// prevent keyboard shortcuts from happening while recording...
			const keycodeValue = getNormalizedKeycode(event);
			if (keycodeValue === 'tab') {
				return;
			}
			event.stopPropagation();
			setIsRecording(true);
			setPressedKeycodes({
				...pressedKeycodes,
				[keycodeValue]: true,
			});
			setCurrentlyPressedKeycodes({
				...currentlyPressedKeycodes,
				[keycodeValue]: true,
			});
		};
		const onFocus: React.FocusEventHandler<HTMLInputElement> = event => {
			setIsRecording(true);
			setCurrentlyPressedKeycodes({});
			setPressedKeycodes({});
			// select whole text
			event.target.setSelectionRange(0, event.target.value.length);
		};
		const onBlur: React.FocusEventHandler<HTMLInputElement> = _event => {
			setIsRecording(false);
		};
		function pressedAtLeastOneNonMetaKey() {
			return Object.keys(pressedKeycodes).some(keycode => {
				// if we find a non-modifier key, then it is valid
				return !['meta', 'ctrl', 'alt', 'shift'].includes(keycode);
			});
		}
		const onKeyUp: React.KeyboardEventHandler<HTMLInputElement> = event => {
			// prevent keyboard shortcuts from happening while recording...
			event.stopPropagation();
			const keycodeValue = getNormalizedKeycode(event);
			// finished
			if (Object.keys(currentlyPressedKeycodes).length === 1) {
				// do we have a valid shortcut?
				const isValid = pressedAtLeastOneNonMetaKey();
				// && pressedNoMoreThanOneNonMetaKey()
				if (isValid) {
					onComplete(pressedKeysToString(pressedKeycodes));
					setCurrentlyPressedKeycodes({});
					setPressedKeycodes({});
					setIsRecording(false);
					return;
					// finish
				} else {
					setCurrentlyPressedKeycodes({});
					setPressedKeycodes({});
					return;
				}
			}
			setCurrentlyPressedKeycodes(removeKeyFromObject(currentlyPressedKeycodes, keycodeValue));
		};
		const value: string = isRecording ? '' : shortcutToString(state.keys) ?? '';
		return (
			<Wrapper className={className}>
				{state.keys !== state.initialKeys ? (
					<>
						<NarrowWrapper>
							<Input
								type="text"
								onFocus={onFocus}
								onBlur={onBlur}
								onKeyDown={onKeyDown}
								onKeyUp={onKeyUp}
								// @ts-ignore
								value={value}
								warning={hasConflict}
								disabled={isOverridable === false}
								placeholder={'Press key'}
								{...restWithoutClassName}
							/>
						</NarrowWrapper>
						<Tooltip content="Reset to default" placement="bottom">
							<Button aria-label="reset" iconBefore="reset" theme="tertiary" {...resetButtonProps} />
						</Tooltip>
					</>
				) : <Input
					type="text"
					onFocus={onFocus}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					onKeyUp={onKeyUp}
					// @ts-ignore
					value={value}
					warning={hasConflict}
					disabled={isOverridable === false}
					placeholder={'Press key'}
					{...restWithoutClassName}
				/>}
			</Wrapper>
		);
	};
